import { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Loader2,
  AlertCircle,
  Bell,
  X,
  Plus,
  RefreshCw,
} from 'lucide-react';
import {
  useGetCuratedNotificationRecipients,
  useUpdateCuratedNotificationRecipients,
} from '../../api/curated-integrations.api';
import { toast } from 'sonner';

const MAX_RECIPIENTS = 50;

/** Simple email regex — same level of validation as backend (IsEmail). */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

/**
 * Seite / Bereich für Benachrichtigungsempfänger bei Curated-Integration-Änderungen.
 * Route: /curated-notifications
 *
 * Zeigt eine Chip-Liste der konfigurierten E-Mail-Adressen.
 * Neue Adressen werden per Eingabefeld + Enter oder Komma hinzugefügt.
 * Max. 50 Empfänger (Backend-Limit).
 */
export function CuratedNotificationsPage() {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<string | undefined>();
  const [isDirty, setIsDirty] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    data: notificationsData,
    isLoading: loadingData,
    error: loadError,
    refetch,
  } = useGetCuratedNotificationRecipients();

  const { mutateAsync: updateRecipients, isLoading: saving, error: saveError } =
    useUpdateCuratedNotificationRecipients();

  // Initialise recipient list from fetched data
  useEffect(() => {
    if (notificationsData?.recipients) {
      setRecipients(notificationsData.recipients);
      setIsDirty(false);
    }
  }, [notificationsData]);

  // ── Chip helpers ───────────────────────────────────────────────────────────

  const addEmail = (raw: string) => {
    const email = raw.trim().toLowerCase();
    setInputError(undefined);

    if (!email) return;

    if (!isValidEmail(email)) {
      setInputError('Ungültige E-Mail-Adresse.');
      return;
    }
    if (recipients.includes(email)) {
      setInputError('Diese E-Mail-Adresse ist bereits in der Liste.');
      return;
    }
    if (recipients.length >= MAX_RECIPIENTS) {
      setInputError(`Maximal ${MAX_RECIPIENTS} Empfänger erlaubt.`);
      return;
    }

    setRecipients((prev) => [...prev, email]);
    setInputValue('');
    setIsDirty(true);
  };

  const removeEmail = (email: string) => {
    setRecipients((prev) => prev.filter((r) => r !== email));
    setIsDirty(true);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === 'Backspace' && inputValue === '' && recipients.length > 0) {
      // Remove last chip on backspace if input is empty
      const last = recipients[recipients.length - 1];
      removeEmail(last);
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    // Commit any uncommitted input
    if (inputValue.trim()) {
      addEmail(inputValue);
      // Return — the state update is async; user can click Save again
      return;
    }

    try {
      await updateRecipients({ recipients });
      toast.success('Empfängerliste gespeichert.');
      setIsDirty(false);
    } catch {
      // saveError rendered below
    }
  };

  const handleDiscard = () => {
    if (notificationsData?.recipients) {
      setRecipients(notificationsData.recipients);
    }
    setInputValue('');
    setInputError(undefined);
    setIsDirty(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-blue-600" aria-hidden="true" />
            Integration-Benachrichtigungen
          </h1>
          <p className="text-gray-500 mt-1">
            E-Mail-Adressen, die bei Änderungen an kuratierten Integrationen benachrichtigt werden.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void refetch()}
          aria-label="Empfängerliste aktualisieren"
        >
          <RefreshCw className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>

      {/* Load error */}
      {loadError && (
        <div
          role="alert"
          className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
        >
          <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          <span className="text-sm">Fehler beim Laden: {loadError.message}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => void refetch()}
            aria-label="Erneut versuchen"
          >
            Wiederholen
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Empfänger</CardTitle>
          <CardDescription>
            Maximal {MAX_RECIPIENTS} E-Mail-Adressen. Empfänger werden bei neuen Methoden-Änderungen und
            Schema-Updates benachrichtigt.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingData ? (
            <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Empfänger werden geladen…
            </div>
          ) : (
            <>
              {/* Chip list */}
              <div
                className="min-h-[48px] flex flex-wrap gap-2 p-3 rounded-lg border border-gray-200 bg-gray-50 cursor-text"
                onClick={() => inputRef.current?.focus()}
                role="group"
                aria-label="E-Mail-Empfänger"
              >
                {recipients.map((email) => (
                  <Badge
                    key={email}
                    variant="outline"
                    className="flex items-center gap-1 bg-white text-gray-700 pr-1"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeEmail(email); }}
                      className="ml-1 rounded-full p-0.5 hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-colors"
                      aria-label={`${email} entfernen`}
                    >
                      <X className="w-3 h-3 text-gray-500" aria-hidden="true" />
                    </button>
                  </Badge>
                ))}

                {/* Inline input */}
                <input
                  ref={inputRef}
                  type="email"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    setInputError(undefined);
                  }}
                  onKeyDown={handleInputKeyDown}
                  onBlur={() => { if (inputValue.trim()) addEmail(inputValue); }}
                  placeholder={recipients.length === 0 ? 'E-Mail-Adresse eingeben…' : ''}
                  disabled={recipients.length >= MAX_RECIPIENTS}
                  className="flex-1 min-w-[200px] bg-transparent text-sm outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
                  aria-label="Neue E-Mail-Adresse eingeben"
                  aria-describedby={inputError ? 'notifications-input-error' : 'notifications-hint'}
                />
              </div>

              {inputError && (
                <p id="notifications-input-error" role="alert" className="text-xs text-red-600">
                  {inputError}
                </p>
              )}

              <p id="notifications-hint" className="text-xs text-gray-400">
                Enter oder Komma drücken um eine Adresse hinzuzufügen.{' '}
                {recipients.length}/{MAX_RECIPIENTS} Empfänger.
              </p>

              {/* Add button (alternative to Enter) */}
              <div className="flex items-center gap-2">
                <Label htmlFor="notifications-add-input" className="sr-only">
                  E-Mail hinzufügen
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="notifications-add-input"
                    type="email"
                    placeholder="Oder hier eingeben und hinzufügen"
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setInputError(undefined);
                    }}
                    onKeyDown={handleInputKeyDown}
                    disabled={recipients.length >= MAX_RECIPIENTS}
                    className="w-72"
                    aria-label="E-Mail-Adresse für Benachrichtigungen"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addEmail(inputValue)}
                    disabled={!inputValue.trim() || recipients.length >= MAX_RECIPIENTS}
                    aria-label="E-Mail hinzufügen"
                  >
                    <Plus className="w-4 h-4 mr-1" aria-hidden="true" />
                    Hinzufügen
                  </Button>
                </div>
              </div>

              {/* Save error */}
              {saveError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  {saveError.message}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="text-xs text-gray-400">
                  {isDirty ? 'Nicht gespeicherte Änderungen vorhanden.' : 'Alle Änderungen gespeichert.'}
                </div>
                <div className="flex gap-3">
                  {isDirty && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDiscard}
                      disabled={saving}
                      aria-label="Änderungen verwerfen"
                    >
                      Verwerfen
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !isDirty}
                    aria-label="Empfängerliste speichern"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
                    Speichern
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
